// API client for the Watchlist & Impersonation Protection module.
// Mirrors apps/api/routers/watchlist.py (mounted at /watchlist) plus the
// brand-protection endpoints in main.py (mounted at /threat-monitoring/
// brand-protection, outside the /watchlist prefix but logically part of
// this module — kept in one client rather than a second file).
import { apiRequest as req } from '@/lib/apiRequest';
import type {
  KeywordSetCreate, KeywordSetUpdate, KeywordSetRecord, KeywordHit,
  KeywordTestMatchRequest, KeywordTestMatchResult,
  BrandProfileCreate, BrandProfileUpdate, BrandProfileRecord,
  VIPProfileCreate, VIPProfileUpdate, VIPProfileRecord, VipDetectedCandidate,
  TakedownCaseCreate, TakedownCaseUpdate, TakedownCaseRecord, EvidenceAttachment,
  ImpersonationScoreRequest, CandidateScore,
  BrandDetectionResult, BrandProtectionStatistics, ThreatIndicator,
  SourceUsageSummary, WatchlistOverviewStats,
} from '@/types/watchlist';

const BASE = '/watchlist';

export const watchlistApi = {
  // ---------- Keyword sets ----------
  createKeywordSet: (body: KeywordSetCreate) =>
    req<KeywordSetRecord>(`${BASE}/keyword-sets`, { method: 'POST', body: JSON.stringify(body) }),
  listKeywordSets: () =>
    req<{ keyword_sets: KeywordSetRecord[]; total: number }>(`${BASE}/keyword-sets`),
  getKeywordSet: (id: string) => req<KeywordSetRecord>(`${BASE}/keyword-sets/${id}`),
  updateKeywordSet: (id: string, body: KeywordSetUpdate) =>
    req<KeywordSetRecord>(`${BASE}/keyword-sets/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteKeywordSet: (id: string) =>
    req<{ deleted: boolean }>(`${BASE}/keyword-sets/${id}`, { method: 'DELETE' }),
  listKeywordSetHits: (id: string, limit = 50) =>
    req<{ hits: KeywordHit[]; total: number }>(`${BASE}/keyword-sets/${id}/hits?limit=${limit}`),
  listAllHits: (limit = 50) =>
    req<{ hits: KeywordHit[]; total: number }>(`${BASE}/hits?limit=${limit}`),
  testKeywordSetMatch: (body: KeywordTestMatchRequest) =>
    req<KeywordTestMatchResult>(`${BASE}/keyword-sets/test-match`, { method: 'POST', body: JSON.stringify(body) }),

  // ---------- Brand profiles ----------
  createBrandProfile: (body: BrandProfileCreate) =>
    req<BrandProfileRecord>(`${BASE}/brand-profiles`, { method: 'POST', body: JSON.stringify(body) }),
  listBrandProfiles: () =>
    req<{ brand_profiles: BrandProfileRecord[]; total: number }>(`${BASE}/brand-profiles`),
  getBrandProfile: (id: string) => req<BrandProfileRecord>(`${BASE}/brand-profiles/${id}`),
  updateBrandProfile: (id: string, body: BrandProfileUpdate) =>
    req<BrandProfileRecord>(`${BASE}/brand-profiles/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteBrandProfile: (id: string) =>
    req<{ deleted: boolean }>(`${BASE}/brand-profiles/${id}`, { method: 'DELETE' }),
  uploadBrandLogo: (id: string, file: File) => {
    const form = new FormData();
    form.append('logo', file);
    return req<BrandProfileRecord>(`${BASE}/brand-profiles/${id}/reference-logos`, { method: 'POST', body: form });
  },
  deleteBrandLogo: (id: string, assetId: string) =>
    req<BrandProfileRecord>(`${BASE}/brand-profiles/${id}/reference-logos/${assetId}`, { method: 'DELETE' }),

  // ---------- VIP profiles ----------
  createVipProfile: (body: VIPProfileCreate) =>
    req<VIPProfileRecord>(`${BASE}/vip-profiles`, { method: 'POST', body: JSON.stringify(body) }),
  listVipProfiles: () =>
    req<{ vip_profiles: VIPProfileRecord[]; total: number }>(`${BASE}/vip-profiles`),
  getVipProfile: (id: string) => req<VIPProfileRecord>(`${BASE}/vip-profiles/${id}`),
  updateVipProfile: (id: string, body: VIPProfileUpdate) =>
    req<VIPProfileRecord>(`${BASE}/vip-profiles/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteVipProfile: (id: string) =>
    req<{ deleted: boolean }>(`${BASE}/vip-profiles/${id}`, { method: 'DELETE' }),
  uploadVipPhoto: (id: string, file: File) => {
    const form = new FormData();
    form.append('photo', file);
    return req<VIPProfileRecord>(`${BASE}/vip-profiles/${id}/reference-photos`, { method: 'POST', body: form });
  },
  deleteVipPhoto: (id: string, assetId: string) =>
    req<VIPProfileRecord>(`${BASE}/vip-profiles/${id}/reference-photos/${assetId}`, { method: 'DELETE' }),
  // Results of watchlist_monitor_worker.py's automatic background scan —
  // impersonation_only=true (default) matches what the email alert fires on.
  listVipDetectedCandidates: (id: string, impersonationOnly = true, limit = 50) =>
    req<{ candidates: VipDetectedCandidate[]; total: number }>(
      `${BASE}/vip-profiles/${id}/detected-candidates?impersonation_only=${impersonationOnly}&limit=${limit}`,
    ),
  reviewVipDetectedCandidate: (profileId: string, candidateId: string, body: { status?: string; overridden_classification?: string }) =>
    req<VipDetectedCandidate>(`${BASE}/vip-profiles/${profileId}/detected-candidates/${candidateId}`, { method: 'PATCH', body: JSON.stringify(body) }),

  // ---------- Impersonation scoring ----------
  scoreImpersonation: (body: ImpersonationScoreRequest) =>
    req<CandidateScore>(`${BASE}/impersonation/score`, { method: 'POST', body: JSON.stringify(body) }),

  // ---------- Takedown tracker ----------
  createTakedownCase: (body: TakedownCaseCreate) =>
    req<TakedownCaseRecord>(`${BASE}/takedown-cases`, { method: 'POST', body: JSON.stringify(body) }),
  listTakedownCases: (params: { status?: string; profile_id?: string } = {}) => {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.profile_id) qs.set('profile_id', params.profile_id);
    const q = qs.toString();
    return req<{ cases: TakedownCaseRecord[]; total: number }>(`${BASE}/takedown-cases${q ? `?${q}` : ''}`);
  },
  getTakedownCase: (id: string) => req<TakedownCaseRecord>(`${BASE}/takedown-cases/${id}`),
  updateTakedownCase: (id: string, body: TakedownCaseUpdate) =>
    req<TakedownCaseRecord>(`${BASE}/takedown-cases/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deleteTakedownCase: (id: string) =>
    req<{ deleted: boolean }>(`${BASE}/takedown-cases/${id}`, { method: 'DELETE' }),
  addTakedownEvidence: (id: string, body: EvidenceAttachment) =>
    req<TakedownCaseRecord>(`${BASE}/takedown-cases/${id}/evidence`, { method: 'POST', body: JSON.stringify(body) }),
  captureTakedownScreenshot: (id: string, url: string) =>
    req<TakedownCaseRecord>(`${BASE}/takedown-cases/${id}/evidence/screenshot`, { method: 'POST', body: JSON.stringify({ url }) }),
  downloadTakedownEvidencePdf: (id: string) =>
    req<Blob>(`${BASE}/takedown-cases/${id}/evidence/pdf`, { responseType: 'blob' }),

  // ---------- Brand protection (main.py, not under /watchlist) ----------
  // Individual threat docs (not just the statistics() aggregate counts) —
  // surfaces watchlist_monitor_worker.py's automatic scan results.
  listBrandProtectionThreats: (brandProfileId?: string, limit = 50) => {
    const qs = new URLSearchParams();
    if (brandProfileId) qs.set('brand_profile_id', brandProfileId);
    qs.set('limit', String(limit));
    return req<{ threats: ThreatIndicator[]; total: number }>(`/threat-monitoring/brand-protection/threats?${qs.toString()}`);
  },
  detectBrandImpersonation: (params: { content: string; source_platform: string; entity_id?: string; brand_profile_id?: string }) => {
    const qs = new URLSearchParams();
    qs.set('content', params.content);
    qs.set('source_platform', params.source_platform);
    if (params.entity_id) qs.set('entity_id', params.entity_id);
    if (params.brand_profile_id) qs.set('brand_profile_id', params.brand_profile_id);
    return req<BrandDetectionResult>(`/threat-monitoring/brand-protection/detect?${qs.toString()}`, { method: 'POST' });
  },
  detectBrandImpersonationWithImage: (params: { image: File; content?: string; source_platform: string; entity_id?: string; brand_profile_id?: string }) => {
    const form = new FormData();
    form.append('image', params.image);
    form.append('content', params.content ?? '');
    form.append('source_platform', params.source_platform);
    if (params.entity_id) form.append('entity_id', params.entity_id);
    if (params.brand_profile_id) form.append('brand_profile_id', params.brand_profile_id);
    return req<BrandDetectionResult>(`/threat-monitoring/brand-protection/detect-with-image`, { method: 'POST', body: form });
  },
  // The real response is {statistics: {...}, time_period, generated_at} —
  // unwrapped here so every caller just gets the inner statistics object.
  getBrandProtectionStatistics: async (params: { time_period?: number; brand_profile_id?: string } = {}) => {
    const qs = new URLSearchParams();
    qs.set('time_period', String(params.time_period ?? 30));
    if (params.brand_profile_id) qs.set('brand_profile_id', params.brand_profile_id);
    const resp = await req<{ statistics: BrandProtectionStatistics }>(`/threat-monitoring/brand-protection/statistics?${qs.toString()}`);
    return resp.statistics;
  },

  // ---------- Sources & alerts usage ----------
  getSourceUsage: () => req<{ usage: Record<string, SourceUsageSummary> }>(`${BASE}/sources/usage`),

  // ---------- Overview tab aggregate stats (main.py, not under /watchlist) ----------
  getOverviewStats: () => req<WatchlistOverviewStats>('/watchlist/overview-stats'),
};
